import {kitItems, kitTotal, relatedCartLines, visibleCartCount} from './commerce.js';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const $ = (selector, root = document) => root.querySelector(selector);
const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];
const root = document.body.dataset.root || '/';
const parse = html => new DOMParser().parseFromString(html, 'text/html');
const isMobile = () => matchMedia('(max-width: 639px)').matches;
const GENERIC_ERROR = 'Noe gikk galt. Prøv igjen.';

function money(cents) {
  const currency = document.body.dataset.currency || 'NOK';
  if (currency === 'NOK') return `${new Intl.NumberFormat('nb-NO', {maximumFractionDigits: 2}).format(cents / 100)}\u00a0kr`;
  return new Intl.NumberFormat(document.documentElement.lang || 'nb-NO', {style: 'currency', currency}).format(cents / 100);
}

let toastTimer;
function toast(message) {
  const el = $('[data-toast]');
  el.textContent = message;
  el.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => (el.hidden = true), 5000);
}

function showError(container, error) {
  const target = container && $('[data-product-error], [data-cart-error]', container);
  const message = error.message || GENERIC_ERROR;
  if (!target) return toast(message);
  target.hidden = false;
  target.textContent = message;
}

async function jsonRequest(path, body) {
  const options = {credentials: 'same-origin', headers: {Accept: 'application/json'}};
  if (body !== undefined) Object.assign(options, {method: 'POST', body: JSON.stringify(body), headers: {...options.headers, 'Content-Type': 'application/json'}});
  const response = await fetch(root + path, options);
  const data = await response.json();
  if (!response.ok || data.status >= 400) throw new Error(data.description || data.message || 'Kunne ikke oppdatere handlekurven. Prøv igjen.');
  return data;
}

function openDialog(dialog) {
  if (dialog && !dialog.open) dialog.showModal();
}

// Fetch a rendered section from a URL and return the element matching `selector`.
// If the section render fails, the full page is fetched instead: `shopify theme dev` answers
// `section_id` requests that carry storefront cookies with a 404, while full pages render.
async function fetchSection(url, selector, signal) {
  let response = await fetch(url, {signal});
  if (!response.ok) {
    const page = new URL(url, location.origin);
    if (!page.searchParams.has('section_id')) throw new Error();
    page.searchParams.delete('section_id');
    response = await fetch(page, {signal});
    if (!response.ok) throw new Error();
  }
  const html = await response.text();
  const element = $(selector, parse(html));
  if (!element) throw new Error();
  return element;
}

// ---------------------------------------------------------------------------
// Global interactions: dialogs, cookie preferences, mobile menu, quantity steppers
// ---------------------------------------------------------------------------
document.addEventListener('click', event => {
  const privacy = event.target.closest('[data-cookie-preferences]');
  if (privacy && window.privacyBanner?.showPreferences) {
    event.preventDefault();
    window.privacyBanner.showPreferences().catch(() => location.assign(privacy.href));
  }

  const opener = event.target.closest('[data-open-dialog]');
  if (opener) openDialog(document.getElementById(opener.dataset.openDialog));

  const closer = event.target.closest('[data-close-dialog]');
  if (closer) closer.closest('dialog')?.close();

  // Clicking the backdrop closes a modal dialog.
  if (event.target instanceof HTMLDialogElement) {
    const {left, right, top, bottom} = event.target.getBoundingClientRect();
    if (event.clientX < left || event.clientX > right || event.clientY < top || event.clientY > bottom) event.target.close();
  }

  if (event.target.closest('[data-menu-open]')) setMenu(true);
  if (event.target.closest('[data-menu-close]')) setMenu(false);

  const step = event.target.closest('[data-quantity-step]');
  if (step) {
    const input = $('input', step.closest('[data-quantity]'));
    const min = Number(input.min || 0);
    const max = Number(input.max || 9999);
    input.value = Math.max(min, Math.min(max, (Number(input.value) || min) + Number(step.dataset.quantityStep)));
    input.dispatchEvent(new Event('change', {bubbles: true}));
  }
});

document.addEventListener('keydown', event => {
  if (event.key !== 'Escape') return;
  if ($('[data-menu]')?.hasAttribute('data-open')) setMenu(false);
  closeArchiveFilters();
});

// ---------------------------------------------------------------------------
// Mobile menu: a full-screen panel; focus moves in on open and back to the button on close.
// ---------------------------------------------------------------------------
function setMenu(open) {
  const menu = $('[data-menu]');
  const button = $('[data-menu-open]');
  if (!menu) return;
  menu.toggleAttribute('data-open', open);
  button?.setAttribute('aria-expanded', String(open));
  if (open) setTimeout(() => $('[data-menu-close]', menu)?.focus(), 50);
  else button?.focus();
}

// ---------------------------------------------------------------------------
// Predictive search: Shopify renders the results section, including current prices.
// ---------------------------------------------------------------------------
let searchTimer;
let searchAbort;
document.addEventListener('input', event => {
  if (!event.target.matches('[data-predictive-input]')) return;
  clearTimeout(searchTimer);
  searchAbort?.abort();
  const term = event.target.value.trim();
  const results = $('[data-predictive-results]');
  if (term.length < 2) {
    results.textContent = 'Skriv minst 2 tegn for å søke.';
    return;
  }
  searchTimer = setTimeout(async () => {
    searchAbort = new AbortController();
    results.textContent = 'Søker...';
    try {
      const params = new URLSearchParams({q: term, 'resources[type]': 'product', 'resources[limit]': '8', section_id: 'predictive-search'});
      const response = await fetch(`${root}search/suggest?${params}`, {signal: searchAbort.signal});
      if (!response.ok) throw new Error();
      const html = parse(await response.text());
      results.innerHTML = $('#shopify-section-predictive-search', html)?.innerHTML || html.body.innerHTML;
    } catch (error) {
      if (error.name !== 'AbortError') results.textContent = 'Kunne ikke laste forslag. Trykk Søk for å se alle resultater.';
    }
  }, 250);
});

// ---------------------------------------------------------------------------
// Cart: every mutation re-renders the drawer and cart page from the server.
// ---------------------------------------------------------------------------
let cartBusy = false;

async function refreshCart({open = false} = {}) {
  const page = $('[data-cart-page]');
  const ids = ['cart-drawer'];
  if (page) ids.push(page.dataset.sectionId);
  const [cart, response] = await Promise.all([
    jsonRequest('cart.js'),
    fetch(`${root}cart?sections=${encodeURIComponent(ids.join(','))}`, {cache: 'no-store'}),
  ]);
  if (!response.ok) throw new Error('Handlekurven er oppdatert, men kunne ikke vises. Åpne handlekurvsiden.');
  const sections = await response.json();

  const oldDrawer = $('#CartDrawer');
  const wasOpen = oldDrawer?.open;
  const newDrawer = $('#CartDrawer', parse(sections['cart-drawer'] || ''));
  if (!newDrawer) throw new Error('Kunne ikke vise handlekurven. Åpne handlekurvsiden.');
  // Swap the contents in place so an open drawer stays open (and doesn't replay its slide-in).
  const hadFocus = oldDrawer.contains(document.activeElement);
  oldDrawer.replaceChildren(...newDrawer.childNodes);
  if (wasOpen || open) openDialog(oldDrawer);
  if (wasOpen && hadFocus && !oldDrawer.contains(document.activeElement)) $('[data-close-dialog]', oldDrawer)?.focus();

  if (page && sections[page.dataset.sectionId]) {
    const next = $('[data-cart-page]', parse(sections[page.dataset.sectionId]));
    if (next) page.replaceWith(next);
  }

  const count = visibleCartCount(cart);
  $$('[data-cart-count]').forEach(el => {
    el.textContent = count;
    el.hidden = count === 0 && !el.hasAttribute('data-cart-count-keep');
  });
  return cart;
}

async function updateCart(key, quantity, control) {
  if (cartBusy) return;
  cartBusy = true;
  const container = control.closest('[data-cart-page], dialog') || document;
  const controls = $$('button, input', container);
  controls.forEach(el => (el.disabled = true));
  const completed = [];
  try {
    const cart = await jsonRequest('cart.js');
    // change.js checks inventory per line, so increase add-ons first and remove the kit last.
    const changes = relatedCartLines(cart, key, quantity).sort((a, b) => (a.key === key ? 1 : 0) - (b.key === key ? 1 : 0));
    for (const change of changes) {
      await jsonRequest('cart/change.js', {id: change.key, quantity: change.quantity});
      completed.push(change);
    }
    await refreshCart();
  } catch (error) {
    // Roll back what succeeded. A failed removal is not recreated; the refresh shows the server's state.
    for (const change of completed.reverse()) {
      if (change.quantity === 0) continue;
      try {
        await jsonRequest('cart/change.js', {id: change.key, quantity: change.before});
      } catch {
        break;
      }
    }
    await refreshCart().catch(() => {});
    showError(control.closest('dialog') ? $('#CartDrawer') : $('[data-cart-page]'), error);
  } finally {
    cartBusy = false;
    controls.forEach(el => (el.disabled = false));
  }
}

document.addEventListener('click', async event => {
  if (event.target.closest('[data-cart-open]')) {
    event.preventDefault();
    try {
      await refreshCart({open: true});
    } catch (error) {
      toast(error.message);
      window.location.href = `${root}cart`;
    }
    return;
  }

  const remove = event.target.closest('[data-cart-remove]');
  if (remove) {
    event.preventDefault();
    await updateCart(remove.dataset.key, 0, remove);
    return;
  }

  const refresh = event.target.closest('[data-cart-refresh]');
  if (refresh) {
    refresh.disabled = true;
    try {
      await refreshCart();
    } catch (error) {
      showError($('[data-cart-page]'), error);
    } finally {
      refresh.disabled = false;
    }
    return;
  }

  const discount = event.target.closest('[data-discount-apply]');
  if (discount) {
    const input = $('#CartDiscount');
    const code = input.value.trim();
    if (!code) return input.focus();
    discount.disabled = true;
    try {
      const cart = await jsonRequest('cart/update.js', {discount: code});
      const applied = cart.discount_codes?.some(item => item.code.toLowerCase() === code.toLowerCase() && item.applicable);
      await refreshCart();
      const message = $('[data-discount-message]');
      if (message) message.textContent = applied ? 'Rabattkoden er lagt til.' : 'Rabattkoden kan ikke brukes på denne handlekurven.';
    } catch (error) {
      showError($('[data-cart-page]'), error);
    } finally {
      discount.disabled = false;
    }
  }
});

let noteTimer;
document.addEventListener('change', event => {
  if (event.target.matches('[data-cart-quantity]')) updateCart(event.target.dataset.key, Number(event.target.value), event.target);
  if (event.target.matches('[data-cart-note]')) {
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => jsonRequest('cart/update.js', {note: event.target.value}).catch(error => showError($('[data-cart-page]'), error)), 300);
  }
});

// ---------------------------------------------------------------------------
// Product page: variants, yarn swatches, kit rows and add-ons
// ---------------------------------------------------------------------------

function choiceData(choice) {
  if (!choice) return null;
  const {id, price, name, displayName, image} = choice.dataset;
  return {id, price: Number(price), name, displayName, image, available: !choice.disabled};
}

function kitRows(product) {
  return $$('[data-kit-row]', product).map(row => ({
    label: row.dataset.label,
    selected: choiceData($('[data-kit-choice][aria-pressed="true"]', row)),
    fallback: choiceData($('[data-kit-choice]:not(:disabled)', row)),
    extra: $('[data-extra-toggle]', row).getAttribute('aria-pressed') === 'true',
  }));
}

function printData(product) {
  const input = $('[data-pattern-print]', product);
  return input?.checked ? {id: input.dataset.id, price: Number(input.dataset.price)} : null;
}

function updateTotal(product) {
  const quantity = Number($('[name="quantity"]', product)?.value || 1);
  const rows = kitRows(product);
  const print = printData(product);
  const total = kitTotal(product.dataset.price, rows, print, quantity);
  $$('[data-product-total], [data-sticky-total]', product).forEach(el => (el.textContent = money(total)));
  const compare = $('[data-compare-total]', product);
  if (compare) compare.textContent = money(kitTotal(product.dataset.comparePrice, rows, print, quantity));
  const label = $('[data-summary-label]', product);
  if (label) label.textContent = rows.some(row => row.extra) || print ? 'Totalt med tillegg' : 'Strikkepakke totalt';
}

function setExtra(button, pressed) {
  button.setAttribute('aria-pressed', String(pressed));
}

function selectKitChoice(row, choice) {
  $$('[data-kit-choice]', row).forEach(button => button.setAttribute('aria-pressed', String(button === choice)));
  const data = choiceData(choice);
  $('[data-kit-current-name]', row).textContent = data?.displayName || data?.name || 'Velg farge';
  $('[data-kit-property]', row).value = data?.name || '';
  const extraPrice = $('[data-kit-extra-price]', row);
  if (extraPrice && data) extraPrice.textContent = `+ ${money(data.price)}`;
  const changed = $('[data-kit-changed]', row);
  if (changed) {
    changed.hidden = !data || data.id === row.dataset.defaultId;
    changed.textContent = `Byttet fra ${row.dataset.defaultName?.toLocaleLowerCase('nb') || 'originalfargen'}`;
  }
  const picker = row.closest('[data-kit-picker]');
  const reset = picker && $('[data-kit-reset]', picker);
  if (reset) reset.hidden = $$('[data-kit-row]', picker).every(r => ($('[data-kit-choice][aria-pressed="true"]', r)?.dataset.id || '') === (r.dataset.defaultId || ''));
  const image = $('[data-kit-current-image]', row);
  image.replaceChildren();
  if (data?.image) {
    const img = document.createElement('img');
    img.src = data.image;
    img.alt = data.name;
    image.append(img);
  }
}

function defaultChoice(row) {
  return row.dataset.defaultId ? $(`[data-kit-choice][data-id="${CSS.escape(row.dataset.defaultId)}"]`, row) : null;
}

// Yarn swatches: filter by search text, and on small screens show the first 12 until expanded.
function filterSwatches(option) {
  const query = $('[data-swatch-search]', option)?.value.toLocaleLowerCase('nb').trim() || '';
  const button = $('[data-swatches-toggle]', option);
  const expanded = button?.getAttribute('aria-expanded') === 'true';
  const mobile = isMobile();
  $$('[data-option-label]', option).forEach((label, index) => {
    if (query) label.hidden = !label.dataset.optionLabel.toLocaleLowerCase('nb').includes(query);
    else label.hidden = mobile && !expanded && index >= 12 && !$('input', label).checked;
  });
  if (button) {
    button.hidden = !mobile || Boolean(query);
    button.textContent = expanded ? 'Vis færre farger' : 'Vis alle farger';
  }
}

let variantAbort;
async function changeVariant(product) {
  variantAbort?.abort();
  variantAbort = new AbortController();
  const values = $$('[data-option-value-id]:checked', product).map(input => input.dataset.optionValueId);
  const url = new URL(product.dataset.productUrl, location.origin);
  url.searchParams.set('option_values', values.join(','));
  url.searchParams.set('section_id', product.dataset.sectionId);

  // State to carry over to the re-rendered section.
  const focusedValue = document.activeElement?.dataset.optionValueId;
  const quantity = $('[name="quantity"]', product).value;
  const swatchQuery = $('[data-swatch-search]', product)?.value;
  const swatchesExpanded = $('[data-swatches-toggle]', product)?.getAttribute('aria-expanded');
  const addons = $('[data-kit-addons]', product);

  product.setAttribute('aria-busy', 'true');
  try {
    const next = await fetchSection(url, '[data-product-root]', variantAbort.signal).catch(() => {
      throw new Error('Kunne ikke laste varianten. Prøv igjen.');
    });
    if (addons && $('[data-kit-addons]', next)) $('[data-kit-addons]', next).replaceWith(addons);
    $('[name="quantity"]', next).value = quantity;
    if (swatchQuery !== undefined && $('[data-swatch-search]', next)) $('[data-swatch-search]', next).value = swatchQuery;
    if (swatchesExpanded) $('[data-swatches-toggle]', next)?.setAttribute('aria-expanded', swatchesExpanded);
    product.replaceWith(next);
    initProduct(next);

    const historyUrl = new URL(location.href);
    historyUrl.searchParams.delete('option_values');
    if (next.dataset.variantId) historyUrl.searchParams.set('variant', next.dataset.variantId);
    else historyUrl.searchParams.set('option_values', values.join(','));
    history.replaceState({}, '', historyUrl);
    if (focusedValue) $(`[data-option-value-id="${CSS.escape(focusedValue)}"]`, next)?.focus({preventScroll: true});
  } catch (error) {
    if (error.name !== 'AbortError') showError(product, error);
  } finally {
    product.removeAttribute('aria-busy');
  }
}

let stickyObserver;
function initProduct(product) {
  if (!product) return;
  $$('[data-kit-row]', product).forEach(row => {
    if (!$('[data-kit-choice][aria-pressed="true"]', row)) selectKitChoice(row, defaultChoice(row));
  });
  $$('[data-option]', product).forEach(filterSwatches);
  stickyObserver?.disconnect();
  const submit = $('[data-product-submit]', product);
  const bar = $('[data-sticky-buy]', product);
  if (submit && bar) {
    stickyObserver = new IntersectionObserver(([entry]) => (bar.hidden = entry.isIntersecting || entry.boundingClientRect.bottom > 0));
    stickyObserver.observe(submit);
  }
  updateTotal(product);
  initColourways(product);
  // The "Bytt farge" swatches sit in closed panels, where lazy images would only start loading when opened.
  whenIdle(() => preloadPalettes(product));
}

function preloadPalettes(scope) {
  $$('[data-kit-options] img[loading="lazy"]', scope).forEach(image => (image.loading = 'eager'));
}

// A shopper heading for "Bytt farge" before the idle preload gets that row's swatches straight away.
document.addEventListener('pointerover', event => {
  const toggle = event.target.closest?.('[data-kit-row-toggle]');
  if (toggle) preloadPalettes(toggle.closest('[data-kit-row]'));
});

document.addEventListener('change', event => {
  const product = event.target.closest('[data-product-root]');
  if (!product) return;
  if (event.target.matches('[data-option-value-id]')) return changeVariant(product);
  if (event.target.matches('[data-extra-all]')) $$('[data-extra-toggle]', product).forEach(button => setExtra(button, event.target.checked));
  updateTotal(product);
});

document.addEventListener('input', event => {
  if (event.target.matches('[data-swatch-search]')) filterSwatches(event.target.closest('[data-option]'));
  if (event.target.matches('[data-kit-search]')) {
    const panel = event.target.closest('[data-kit-options]');
    const query = event.target.value.toLocaleLowerCase('nb');
    let count = 0;
    $$('[data-kit-choice]', panel).forEach(button => {
      button.hidden = !button.dataset.name.toLocaleLowerCase('nb').includes(query);
      if (!button.hidden) count++;
    });
    $('[data-kit-no-results]', panel).hidden = count > 0;
  }
});

document.addEventListener('click', event => {
  const product = event.target.closest('[data-product-root]');
  if (!product) return;

  const thumb = event.target.closest('[data-gallery-thumb]');
  if (thumb) {
    const image = $('[data-main-image]', product);
    image.src = thumb.dataset.src;
    image.removeAttribute('srcset');
    image.alt = thumb.dataset.alt;
    $$('[data-gallery-thumb]', product).forEach(button => button.setAttribute('aria-pressed', String(button === thumb)));
  }

  if (event.target.closest('[data-gallery-zoom]')) {
    const dialog = $('[data-gallery-dialog]', product);
    $('img', dialog).src = $('[data-main-image]', product).src;
    openDialog(dialog);
  }

  const rowToggle = event.target.closest('[data-kit-row-toggle]');
  if (rowToggle) {
    const row = rowToggle.closest('[data-kit-row]');
    const panel = $('[data-kit-options]', row);
    panel.hidden = !panel.hidden;
    $$('[data-kit-row-toggle]', row).forEach(button => button.setAttribute('aria-expanded', String(!panel.hidden)));
  }

  const choice = event.target.closest('[data-kit-choice]');
  if (choice) {
    const row = choice.closest('[data-kit-row]');
    selectKitChoice(row, choice);
    updateTotal(product);
    // Close the palette after a pick and hand focus back to its toggle.
    const panel = $('[data-kit-options]', row);
    const toggle = $('[data-kit-row-toggle]', row);
    if (panel && toggle) {
      panel.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      toggle.focus();
    }
  }

  const extra = event.target.closest('[data-extra-toggle]');
  if (extra) {
    setExtra(extra, extra.getAttribute('aria-pressed') !== 'true');
    const all = $('[data-extra-all]', product);
    if (all) all.checked = $$('[data-extra-toggle]', product).every(button => button.getAttribute('aria-pressed') === 'true');
    updateTotal(product);
  }

  if (event.target.closest('[data-kit-reset]')) {
    $$('[data-kit-row]', product).forEach(row => selectKitChoice(row, defaultChoice(row)));
    updateTotal(product);
  }

  const swatchesToggle = event.target.closest('[data-swatches-toggle]');
  if (swatchesToggle) {
    swatchesToggle.setAttribute('aria-expanded', String(swatchesToggle.getAttribute('aria-expanded') !== 'true'));
    filterSwatches(swatchesToggle.closest('[data-option]'));
  }

  if (event.target.closest('[data-sticky-submit]')) $('[data-product-form]', product).requestSubmit();
});

document.addEventListener('submit', async event => {
  if (!event.target.matches('[data-product-form]')) return;
  event.preventDefault();
  const form = event.target;
  const product = form.closest('[data-product-root]');
  const button = $('[data-product-submit]', form);
  const errorEl = $('[data-product-error]', form);
  if (button.disabled) return;
  button.disabled = true;
  errorEl.hidden = true;
  const previousLabel = button.textContent;
  button.textContent = 'Legger til...';
  try {
    const data = new FormData(form);
    const properties = {};
    for (const [key, value] of data) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && value) properties[match[1]] = value;
    }
    const quantity = Number(data.get('quantity'));
    let items;
    if (product.dataset.mode === 'kit') {
      properties._mittgarn_title = $('h1', product).textContent;
      items = kitItems({variantId: data.get('id'), quantity, properties, rows: kitRows(product), print: printData(product), group: crypto.randomUUID()});
    } else {
      items = [{id: Number(data.get('id')), quantity, properties}];
    }
    await jsonRequest('cart/add.js', {items});
    // A rendering failure after a successful add must never trigger another add.
    try {
      await refreshCart({open: true});
    } catch {
      toast('Varen er lagt til. Åpne handlekurven for å se bestillingen.');
    }
  } catch (error) {
    showError(form, error);
  } finally {
    button.disabled = product.dataset.available !== 'true';
    button.textContent = previousLabel;
  }
});

// ---------------------------------------------------------------------------
// Kit colourways on the product page: siblings of the same model come from a search rendered by
// templates/search.colourways.liquid. Their pages and main images are preloaded, and choosing one swaps the
// product section in place, so the switch is instant. The address and title follow, and back/forward work.
// ---------------------------------------------------------------------------
const colourwayFamilies = new Map(); // family key → Promise of pill <li> elements
const colourwayPages = new Map(); // path → Promise of {html, recommendations, title}
const pathOf = href => new URL(href, location.origin).pathname;
const whenIdle = window.requestIdleCallback || (callback => setTimeout(callback, 300));

function familyPills(block) {
  const key = block.dataset.family;
  if (!colourwayFamilies.has(key)) {
    const params = new URLSearchParams({q: block.dataset.query, type: 'product', 'options[prefix]': 'last', view: 'colourways'});
    const request = fetch(`${root}search?${params}`)
      .then(response => {
        if (!response.ok) throw new Error();
        return response.text();
      })
      .then(html => $$('[data-colourway]', parse(html)).filter(pill => pill.dataset.family === key))
      .catch(() => {
        colourwayFamilies.delete(key);
        return [];
      });
    colourwayFamilies.set(key, request);
  }
  return colourwayFamilies.get(key);
}

function loadColourway(path) {
  if (!colourwayPages.has(path)) {
    const request = fetch(path)
      .then(response => {
        if (!response.ok) throw new Error();
        return response.text();
      })
      .then(html => {
        const doc = parse(html);
        const next = $('[data-product-root]', doc);
        if (!next) throw new Error();
        // Warm the image cache with the same candidate the page will pick.
        const image = $('[data-main-image]', next);
        if (image) {
          const preload = new Image();
          preload.sizes = image.getAttribute('sizes') || '';
          preload.srcset = image.getAttribute('srcset') || '';
          preload.src = image.getAttribute('src');
        }
        return {html: next.outerHTML, recommendations: $('[data-recommendations]', doc)?.outerHTML, title: doc.title};
      });
    request.catch(() => colourwayPages.delete(path));
    colourwayPages.set(path, request);
  }
  return colourwayPages.get(path);
}

function showColourwayNames(block) {
  const current = $('[data-colourway] a[aria-current]', block)?.closest('[data-colourway]');
  const names = $('[data-colourway-colours]', block);
  if (current && names) names.textContent = current.dataset.colours ? `(${current.dataset.colours})` : '';
}

async function initColourways(product) {
  const block = $('[data-colourways]', product);
  if (!block) return;
  showColourwayNames(block);
  const ownPath = pathOf(product.dataset.productUrl);
  if (!colourwayPages.has(ownPath)) {
    colourwayPages.set(ownPath, Promise.resolve({html: product.outerHTML, recommendations: $('[data-recommendations]')?.outerHTML, title: document.title}));
  }
  const pills = await familyPills(block);
  if (!block.isConnected) return;
  const unique = new Map();
  pills.forEach(pill => unique.set(pathOf($('a', pill).href), pill));
  unique.set(ownPath, unique.get(ownPath) || $('[data-colourway]', block));
  if (unique.size < 2) return block.remove();
  const sorted = [...unique].sort(([, a], [, b]) => a.dataset.letter.localeCompare(b.dataset.letter, 'nb'));
  $('[data-colourway-list]', block).replaceChildren(...sorted.map(([path, pill]) => {
    const copy = pill.cloneNode(true);
    const link = $('a', copy);
    link.href = path;
    if (path === ownPath) link.setAttribute('aria-current', 'true');
    else link.removeAttribute('aria-current');
    return copy;
  }));
  whenIdle(() => sorted.forEach(([path]) => path !== ownPath && loadColourway(path).catch(() => {})));
}

async function showColourway(path, {push = true} = {}) {
  const product = $('[data-product-root]');
  const cached = colourwayPages.has(path);
  if (!cached) product.setAttribute('aria-busy', 'true');
  try {
    const page = await loadColourway(path);
    const next = $('[data-product-root]', parse(page.html));
    product.replaceWith(next);
    const recommendations = $('[data-recommendations]');
    if (recommendations && page.recommendations) {
      const fresh = $('[data-recommendations]', parse(page.recommendations));
      if (fresh) {
        recommendations.replaceWith(fresh);
        initRecommendations();
      }
    }
    document.title = page.title;
    if (push) history.pushState({colourway: true}, '', path);
    initProduct(next);
  } catch {
    location.assign(path);
  }
}

document.addEventListener('click', event => {
  const link = event.target.closest('[data-colourway] a');
  if (!link || event.defaultPrevented || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  if (!link.closest('[data-product-root]')) return;
  event.preventDefault();
  if (link.hasAttribute('aria-current')) return;
  showColourway(pathOf(link.href));
});

// Preload on hover or touch too, in case the shopper is faster than the idle preload.
document.addEventListener('pointerover', event => {
  const link = event.target.closest?.('[data-colourway] a:not([aria-current])');
  if (link) loadColourway(pathOf(link.href)).catch(() => {});
});

// ---------------------------------------------------------------------------
// Product cards: kit colourways share a card. Each yarn-ball button swaps the photo, link, price and code.
// Cards for the same model that arrive later (load more) are merged into the first one.
// ---------------------------------------------------------------------------
const MAX_WAYS = 4;

function pickWay(card, button, focus = false) {
  const ways = $$('[data-way]', card);
  ways.forEach(way => {
    way.setAttribute('aria-checked', String(way === button));
    way.tabIndex = way === button ? 0 : -1;
  });
  if (focus) button.focus();
  const image = $('[data-card-img]', card);
  if (image && button.dataset.src) {
    image.srcset = button.dataset.srcset || '';
    image.src = button.dataset.src;
  }
  $('[data-card-link]', card).href = button.dataset.href;
  const price = $('[data-card-price]', card);
  price.textContent = button.dataset.price;
  price.classList.toggle('text-salg', button.dataset.sale === 'true');
  const compare = $('[data-card-compare]', card);
  if (compare) {
    compare.textContent = button.dataset.compare;
    compare.hidden = !button.dataset.compare;
  }
  const code = $('[data-card-code]', card);
  if (code && button.dataset.code) code.textContent = button.dataset.code;
  const badge = $('.sale-badge', card);
  if (badge && Number(button.dataset.percent) > 0) badge.textContent = `−${button.dataset.percent} %`;
  layoutWays(card);
}

// Show at most four buttons; with more, show three (always including the chosen one) and a "+N" link.
function layoutWays(card) {
  const group = $('[data-ways]', card);
  if (!group) return;
  const ways = $$('[data-way]', group);
  $('[data-ways-more]', group)?.remove();
  const single = ways.length === 1;
  group.toggleAttribute('data-single', single);
  ways.forEach(way => {
    way.toggleAttribute('data-single', single);
    way.disabled = single;
  });
  if (single) {
    group.removeAttribute('role');
    ways[0].removeAttribute('role');
    ways[0].tabIndex = -1;
  } else {
    group.setAttribute('role', 'radiogroup');
    ways.forEach(way => way.setAttribute('role', 'radio'));
  }
  const checked = ways.findIndex(way => way.getAttribute('aria-checked') === 'true');
  const visible = ways.length > MAX_WAYS ? MAX_WAYS - 1 : ways.length;
  ways.forEach((way, i) => (way.hidden = i >= visible && i !== checked));
  if (checked >= visible && ways.length > MAX_WAYS) ways[visible - 1].hidden = true;
  if (ways.length > MAX_WAYS) {
    const more = document.createElement('a');
    more.href = $('[data-card-link]', card).href;
    more.dataset.waysMore = '';
    more.tabIndex = -1;
    more.setAttribute('aria-hidden', 'true');
    more.className = 'grid h-10 place-items-center px-1 text-sm font-medium text-skifer hover:text-hav lg:h-11';
    more.textContent = `+${ways.length - (MAX_WAYS - 1)}`;
    group.append(more);
  }
  const count = $('[data-card-count]', card);
  if (count) count.textContent = single ? '' : `, ${ways.length} fargevalg`;
}

function initCards(scope = document) {
  // Merge cards of the same model inside each grid, keeping the first card.
  const grids = new Set($$('[data-card][data-family]', scope).map(card => card.parentElement.closest('ul, [data-archive-grid], div')));
  grids.forEach(grid => {
    const first = new Map();
    $$('[data-card][data-family]', grid).forEach(card => {
      const lead = first.get(card.dataset.family);
      if (!lead) return first.set(card.dataset.family, card);
      const target = $('[data-ways]', lead);
      if (target) {
        $$('[data-way]', card).forEach(way => {
          way.setAttribute('aria-checked', 'false');
          target.append(way);
        });
      }
      const wrapper = card.parentElement.matches('li') && card.parentElement.children.length === 1 ? card.parentElement : card;
      wrapper.remove();
    });
  });
  $$('[data-card]', scope).forEach(card => {
    const ways = $$('[data-way]', card);
    if (!ways.length) return;
    ways.forEach(way => (way.tabIndex = way.getAttribute('aria-checked') === 'true' ? 0 : -1));
    layoutWays(card);
  });
}

// Whole rows only: while a listing has more to load, the cards that would start an unfinished last row wait
// (hidden) and come back first when the next page arrives. Small card rows (bestsellers) never end with a stub row.
function balanceGrid(grid) {
  const cards = [...grid.children];
  cards.forEach(card => {
    if (!card.hasAttribute('data-balance-hidden')) return;
    card.hidden = false;
    card.removeAttribute('data-balance-hidden');
  });
  const style = getComputedStyle(grid);
  if (style.display !== 'grid') return;
  const columns = style.gridTemplateColumns.split(' ').filter(Boolean).length;
  const visible = cards.filter(card => !card.hidden);
  const archive = grid.closest('[data-archive]');
  const more = grid.matches('[data-archive-grid]') ? Boolean(archive && $('[data-load-more]', archive)) : visible.length > columns;
  const extra = visible.length % columns;
  if (!more || columns < 2 || !extra) return;
  visible.slice(-extra).forEach(card => {
    card.hidden = true;
    card.setAttribute('data-balance-hidden', '');
  });
}

function balanceGrids(scope = document) {
  const grids = $$('[data-archive-grid], [data-card-list]', scope);
  if (scope.matches?.('[data-archive-grid], [data-card-list]')) grids.push(scope);
  grids.forEach(balanceGrid);
}

let balanceFrame;
window.addEventListener('resize', () => {
  cancelAnimationFrame(balanceFrame);
  balanceFrame = requestAnimationFrame(() => balanceGrids());
}, {passive: true});

document.addEventListener('click', event => {
  const way = event.target.closest('[data-way]');
  if (way && !way.disabled) pickWay(way.closest('[data-card]'), way);
});

document.addEventListener('keydown', event => {
  const way = event.target.closest('[data-way]');
  if (!way) return;
  const step = {ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1}[event.key];
  if (!step) return;
  event.preventDefault();
  const card = way.closest('[data-card]');
  const ways = $$('[data-way]', card);
  pickWay(card, ways[(ways.indexOf(way) + step + ways.length) % ways.length], true);
});

// Hovering a card loads its other colourways' photos, so a yarn-ball click swaps instantly. The hovered yarn ball
// goes first at high priority. The images use the card photo's `sizes`, so the browser picks the same file the
// swap asks for, and they stay referenced here so the decoded files remain in the memory cache.
const preloadedWays = new Map(); // srcset → Image

function preloadWay(card, way, priority = 'auto') {
  const key = way.dataset.srcset || way.dataset.src;
  if (!key || preloadedWays.has(key) || way.getAttribute('aria-checked') === 'true') return;
  const image = new Image();
  image.fetchPriority = priority;
  image.sizes = $('[data-card-img]', card)?.sizes || '';
  image.srcset = way.dataset.srcset || '';
  image.src = way.dataset.src || '';
  image.decode().catch(() => preloadedWays.delete(key));
  preloadedWays.set(key, image);
}

function preloadWays(event) {
  const card = event.target.closest?.('[data-card]');
  if (!card) return;
  const way = event.target.closest('[data-way]');
  if (way) preloadWay(card, way, 'high');
  // Arrow keys also reach the yarn balls hidden behind "+N", so keyboard focus loads those too.
  $$('[data-way]', card).forEach(other => (event.type === 'focusin' || !other.hidden) && preloadWay(card, other));
}

for (const type of ['pointerover', 'touchstart', 'focusin']) document.addEventListener(type, preloadWays, {passive: true});

// ---------------------------------------------------------------------------
// Archive (collections and search): filter drawer, category tree, in-place loading and history
// ---------------------------------------------------------------------------
function setArchiveFilters(open) {
  const sidebar = $('[data-archive-sidebar]');
  if (!sidebar) return;
  sidebar.toggleAttribute('data-open', open);
  const backdrop = $('[data-filter-backdrop]');
  if (backdrop) backdrop.hidden = !open;
  const toggle = $('[data-filter-toggle]');
  toggle?.setAttribute('aria-expanded', String(open));
  if (open) $('[data-filter-close]', sidebar)?.focus();
  else toggle?.focus();
}

function closeArchiveFilters() {
  if ($('[data-archive-sidebar]')?.hasAttribute('data-open')) setArchiveFilters(false);
}

const archiveHistory = new Map();
function rememberArchive() {
  const archive = $('[data-archive]');
  if (!archive) return;
  const key = history.state?.mittgarnArchive || crypto.randomUUID();
  history.replaceState({...history.state, mittgarnArchive: key}, '', location.href);
  archiveHistory.set(key, {html: archive.outerHTML, scroll: window.scrollY});
  if (archiveHistory.size > 20) archiveHistory.delete(archiveHistory.keys().next().value);
}

let archiveAbort;
const ARCHIVE_PAGE_SIZE = 24; // matches `paginate … by 24` in the collection and search sections
// "Vis flere" appends the next page in place and leaves the address unchanged, so the list is one long page.
// The number of loaded pages is kept in the history entry and reloaded when the shopper comes back.
async function loadArchive(url, {append = false, push = true, overflow} = {}) {
  const archive = $('[data-archive]');
  if (!archive) return;
  if (append) push = false;
  if (push) rememberArchive();
  archiveAbort?.abort();
  archiveAbort = new AbortController();
  archive.setAttribute('aria-busy', 'true');
  const target = new URL(url, location.origin);
  const fetchUrl = new URL(target);
  fetchUrl.searchParams.set('section_id', archive.dataset.sectionId);
  try {
    const next = append ? $('[data-archive]', parse(await fetchArchivePage(target))) : await fetchSection(fetchUrl, '[data-archive]', archiveAbort.signal);
    if (append) {
      const grid = $('[data-archive-grid]', archive);
      const cards = [...$('[data-archive-grid]', next).children];
      if (overflow !== undefined) {
        // The search behind an overflow page lists its products by its own relevance; restore the requested order.
        const order = target.searchParams.get('q').match(/\d+/g);
        const position = card => (order.includes(card.dataset.productId) ? order.indexOf(card.dataset.productId) : order.length);
        cards.sort((a, b) => position(a) - position(b));
      }
      grid.append(...cards);
      initCards(grid);
      $('[data-pagination]', archive).replaceWith($('[data-pagination]', next));
      let shown;
      if (overflow === undefined) {
        archive.dataset.pages = Number(target.searchParams.get('page')) || 1;
        shown = Math.min(archive.dataset.pages * ARCHIVE_PAGE_SIZE, Number(archive.dataset.listedTotal || archive.dataset.resultTotal));
      } else {
        archive.dataset.pages = Number(archive.dataset.pages || 1) + 1;
        archive.dataset.overflowIndex = overflow;
        shown = Math.min(Number(archive.dataset.listedTotal) + Number(overflow), Number(archive.dataset.resultTotal));
      }
      if (archive.hasAttribute('data-search-capped')) await addOverflowLink(archive).catch(() => {});
      balanceGrid(grid);
      setResultCount(archive, shown);
      history.replaceState({...history.state, archivePages: Number(archive.dataset.pages)}, '', location.href);
    } else {
      archive.replaceWith(next);
      initCards(next);
      balanceGrids(next);
      initSearchOverflow(next);
    }
    if (push) history.pushState({archive: true, append}, '', target);
    $('[data-archive-status]').textContent = append ? 'Flere produkter er lastet inn.' : 'Produktene er oppdatert.';
    rememberArchive();
  } catch (error) {
    if (error.name === 'AbortError') return;
    if (append) toast('Kunne ikke laste flere produkter. Prøv igjen.');
    else window.location.href = target;
  } finally {
    archive.removeAttribute('aria-busy');
  }
}

document.addEventListener('click', event => {
  if (event.target.closest('[data-filter-toggle]')) setArchiveFilters(true);
  if (event.target.closest('[data-filter-close]')) closeArchiveFilters();

  const categoryToggle = event.target.closest('[data-category-toggle]');
  if (categoryToggle) {
    event.preventDefault();
    const expanded = categoryToggle.getAttribute('aria-expanded') !== 'true';
    categoryToggle.setAttribute('aria-expanded', String(expanded));
    const children = $(':scope > [data-category-children]', categoryToggle.closest('[data-category-node]'));
    if (children) children.hidden = !expanded;
    return;
  }

  const link = event.target.closest('[data-archive-link], [data-load-more]');
  if (link) {
    event.preventDefault();
    loadArchive(link.href, {append: link.hasAttribute('data-load-more'), overflow: link.dataset.overflowNext});
  }
});

document.addEventListener('submit', event => {
  if (!event.target.matches('[data-filter-form]')) return;
  event.preventDefault();
  const url = new URL(event.target.action);
  for (const [key, value] of new FormData(event.target)) if (value !== '') url.searchParams.append(key, value);
  loadArchive(url);
});

document.addEventListener('change', event => {
  if (!event.target.matches('[data-sort-by]')) return;
  const url = new URL(location.href);
  url.searchParams.set('sort_by', event.target.value);
  url.searchParams.delete('page');
  loadArchive(url);
});

window.addEventListener('popstate', event => {
  if ($('[data-archive]')) {
    const cached = archiveHistory.get(event.state?.mittgarnArchive);
    if (cached) {
      archiveAbort?.abort();
      const archive = $('[data-archive]', parse(cached.html));
      $('[data-archive]').replaceWith(archive);
      initSearchOverflow(archive);
      window.scrollTo(0, cached.scroll);
    } else {
      loadArchive(location.href, {push: false});
    }
  } else if ($('[data-product-root]')) {
    if (colourwayPages.has(location.pathname)) showColourway(location.pathname, {push: false});
    else location.reload();
  }
});

function setResultCount(archive, shown) {
  if (shown !== undefined) archive.dataset.resultShown = shown;
  $$('[data-result-count]', archive).forEach(el => (el.textContent = `Viser ${archive.dataset.resultShown} av ${archive.dataset.resultTotal}`));
}

// Searches past 1000 results. Shopify stops every storefront search at 1000 results, in Liquid and in the Storefront
// API alike, but the Storefront API's product query has no such limit. When a search reaches 1000, the products that
// query finds beyond the ones the search lists are shown after the search's own pages, 24 at a time. Each batch is
// rendered by the search section as an `id:… OR id:…` search, so the cards are the usual Liquid cards.
const SEARCH_ID_QUERIES = {
  search: `query($q: String!, $after: String, $sortKey: SearchSortKeys, $reverse: Boolean) {
    search(query: $q, types: PRODUCT, first: 250, after: $after, sortKey: $sortKey, reverse: $reverse) {
      pageInfo { hasNextPage endCursor } nodes { ... on Product { id } }
    }
  }`,
  products: `query($q: String, $after: String, $sortKey: ProductSortKeys, $reverse: Boolean) {
    products(query: $q, first: 250, after: $after, sortKey: $sortKey, reverse: $reverse) {
      pageInfo { hasNextPage endCursor } nodes { id }
    }
  }`,
};
const searchOverflows = new Map(); // sort + terms → Promise of {listed, ids}

async function storefrontProductIds(field, variables) {
  const ids = [];
  let after = null;
  do {
    const response = await fetch('/api/2026-10/graphql.json', {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({query: SEARCH_ID_QUERIES[field], variables: {...variables, after}}),
    });
    const connection = (await response.json()).data?.[field];
    if (!response.ok || !connection) throw new Error();
    connection.nodes.forEach(node => node.id && ids.push(node.id.split('/').pop()));
    after = connection.pageInfo.hasNextPage ? connection.pageInfo.endCursor : null;
  } while (after);
  return ids;
}

// `listed` is how many products the search's own pages hold, `ids` the matching products after them, in order.
// The product query skips and repeats products when it pages by relevance, so past the search's own pages a search
// by relevance continues with the newest products first.
function searchOverflow(archive) {
  const {searchTerms: q, sortBy} = archive.dataset;
  const key = `${sortBy} ${q}`;
  if (!searchOverflows.has(key)) {
    const byPrice = sortBy.startsWith('price-');
    const reverse = sortBy === 'price-descending';
    const request = Promise.all([
      storefrontProductIds('search', {q, sortKey: byPrice ? 'PRICE' : 'RELEVANCE', reverse}),
      storefrontProductIds('products', {q, sortKey: byPrice ? 'PRICE' : 'ID', reverse: byPrice ? reverse : true}),
    ]).then(([listed, all]) => {
      const seen = new Set(listed);
      return {listed: seen.size, ids: [...new Set(all)].filter(id => !seen.has(id))};
    });
    request.catch(() => searchOverflows.delete(key));
    searchOverflows.set(key, request);
  }
  return searchOverflows.get(key);
}

// Once the search's own pages run out, "Vis flere" moves on to the next batch of products past the limit.
const loadMoreLink = archive => $('[data-pagination] [data-load-more]', archive);

async function addOverflowLink(archive) {
  if (!archive?.hasAttribute('data-search-capped') || loadMoreLink(archive)) return loadMoreLink(archive);
  const {ids} = await searchOverflow(archive);
  if (loadMoreLink(archive) || !archive.isConnected) return loadMoreLink(archive);
  const shown = new Set($$('[data-product-id]', archive).map(el => el.dataset.productId));
  let index = Number(archive.dataset.overflowIndex) || 0;
  const batch = [];
  while (index < ids.length && batch.length < ARCHIVE_PAGE_SIZE) {
    const id = ids[index++];
    if (!shown.has(id)) batch.push(id);
  }
  if (!batch.length) return null;
  const url = new URL(`${root}search`, location.origin);
  url.searchParams.set('type', 'product');
  url.searchParams.set('q', batch.map(id => `id:${id}`).join(' OR '));
  const more = document.createElement('a');
  more.href = url;
  more.className = 'btn btn-secondary';
  more.dataset.loadMore = '';
  more.dataset.overflowNext = index;
  more.textContent = archive.dataset.moreLabel;
  $('[data-pagination]', archive).append(more);
  return more;
}

// A search at the limit shows its full number of results once the products past the limit are known.
async function initSearchOverflow(archive = $('[data-archive]')) {
  if (!archive?.hasAttribute('data-search-capped') || archive.dataset.listedTotal) return;
  try {
    const {listed, ids} = await searchOverflow(archive);
    archive.dataset.listedTotal = listed;
    archive.dataset.resultTotal = listed + ids.length;
    const label = $('[data-result-total-label]', archive);
    if (label) label.textContent = new Intl.NumberFormat('nb-NO').format(listed + ids.length);
    setResultCount(archive);
    if (!loadMoreLink(archive)) {
      await addOverflowLink(archive);
      balanceGrid($('[data-archive-grid]', archive));
    }
  } catch {}
}

// Next pages load ahead: once the shopper scrolls, the page behind "Vis flere" is fetched and its photos are
// warmed in the image cache, so pressing the button appends at once. Each page is fetched only once.
const archivePages = new Map(); // section URL → Promise of the rendered archive HTML

function fetchArchivePage(href) {
  const archive = $('[data-archive]');
  const url = new URL(href, location.origin);
  url.searchParams.set('section_id', archive.dataset.sectionId);
  const key = url.toString();
  if (!archivePages.has(key)) {
    const request = fetchSection(url, '[data-archive]').then(next => {
      $$('[data-archive-grid] img', next).forEach(image => {
        const preload = new Image();
        preload.sizes = image.getAttribute('sizes') || '';
        preload.srcset = image.getAttribute('srcset') || '';
        preload.src = image.getAttribute('src');
      });
      return next.outerHTML;
    });
    request.catch(() => archivePages.delete(key));
    archivePages.set(key, request);
  }
  return archivePages.get(key);
}

let prefetchFrame;
window.addEventListener('scroll', () => {
  if (prefetchFrame) return;
  prefetchFrame = requestAnimationFrame(() => {
    prefetchFrame = null;
    const more = $('[data-load-more]');
    if (more && window.scrollY > 100) fetchArchivePage(more.href).catch(() => {});
  });
}, {passive: true});

// Coming back to a listing (reload or back button): load the pages the shopper had open, then return to their place.
async function restoreArchivePages() {
  const pages = history.state?.archivePages;
  const scroll = history.state?.archiveScroll;
  if (!$('[data-archive]') || !(pages > 1)) return;
  history.scrollRestoration = 'manual';
  for (let page = 2; page <= pages; page++) {
    const more = $('[data-load-more]') || (await addOverflowLink($('[data-archive]')).catch(() => null));
    if (!more) break;
    await loadArchive(more.href, {append: true, overflow: more.dataset.overflowNext});
  }
  if (scroll) window.scrollTo(0, scroll);
}
window.addEventListener('pagehide', () => {
  if ($('[data-archive]')) history.replaceState({...history.state, archiveScroll: window.scrollY}, '', location.href);
});

// ---------------------------------------------------------------------------
// Product recommendations
// ---------------------------------------------------------------------------
function initRecommendations(scope = document) {
  $$('[data-recommendations]', scope).forEach(async element => {
    if (element.dataset.loaded) return;
    element.dataset.loaded = 'true';
    try {
      element.innerHTML = (await fetchSection(element.dataset.url, '[data-recommendations]')).innerHTML;
      initCards(element);
      balanceGrids(element);
    } catch {
      // Recommendations never block shopping.
    }
  });
}

// ---------------------------------------------------------------------------
// Legal pages: build the table of contents from the policy text, including imported WordPress headings.
// ---------------------------------------------------------------------------
function initLegalPage(page) {
  const content = $('[data-legal-content]', page);
  if (page.hasAttribute('data-legal-terms')) promoteTermsHeadings(content);

  const firstParagraph = $('p', content);
  if (firstParagraph?.textContent.trim().startsWith('Sist oppdatert:')) firstParagraph.classList.add('legal-updated');

  const headings = $$('h2', content);
  const index = $('[data-legal-index]', page);
  headings.forEach((heading, i) => {
    heading.id ||= `legal-section-${i + 1}`;
    const link = document.createElement('a');
    link.href = `#${heading.id}`;
    link.textContent = heading.textContent.trim();
    const item = document.createElement('li');
    item.append(link);
    index.append(item);
  });
  const box = $('[data-legal-index-box]', page);
  box.hidden = !headings.length;
  box.open = !matchMedia('(max-width: 767px)').matches;

  const links = $$('a', index);
  let scheduled = false;
  function setActive() {
    let active = 0;
    headings.forEach((heading, i) => {
      if (heading.getBoundingClientRect().top <= 160) active = i;
    });
    links.forEach((link, i) => (i === active ? link.setAttribute('aria-current', 'location') : link.removeAttribute('aria-current')));
  }
  window.addEventListener('scroll', () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      setActive();
      scheduled = false;
    });
  }, {passive: true});
  setActive();
}

// The imported terms use bold paragraphs ("1. Innledning") instead of headings and carry an old index.
function promoteTermsHeadings(content) {
  const paragraphs = $$('p', content);
  const intro = paragraphs.find(p => /^Innledning:?$/.test(p.textContent.trim()));
  const oldIndex = paragraphs.find(p => p.textContent.trim().startsWith('Innholdsfortegnelse:'));
  if (oldIndex && intro) {
    let node = oldIndex;
    while (node && node !== intro) {
      const next = node.nextSibling;
      node.remove();
      node = next;
    }
  }
  for (const p of $$('p', content)) {
    if (!p.firstElementChild?.matches('strong')) continue;
    if (!/^(\d+\.\s|Innledning:)/.test(p.textContent.trim())) continue;
    // A heading can share its paragraph with body text: move only the bold title before the first line break.
    const range = document.createRange();
    range.setStart(p, 0);
    const firstBreak = $('br', p);
    if (firstBreak) range.setEndBefore(firstBreak);
    else range.selectNodeContents(p);
    const heading = document.createElement('h2');
    heading.append(range.extractContents());
    if (!heading.textContent.trim()) continue;
    firstBreak?.remove();
    p.before(heading);
    while (p.firstChild?.nodeName === 'BR') p.firstChild.remove();
    if (!p.textContent.trim()) p.remove();
  }
}

// ---------------------------------------------------------------------------
// Hover prefetch for browsers without speculation rules (snippets/speculation-rules.liquid covers the rest):
// resting on a store link for a moment adds a <link rel="prefetch"> for its page.
// ---------------------------------------------------------------------------
if (!HTMLScriptElement.supports?.('speculationrules')) {
  const SKIP = '[data-no-prefetch], [data-no-prefetch] *, [data-cart-open], [data-cart-remove], [data-load-more], [data-archive-link], [data-colourway] a, [data-cookie-preferences], [target=_blank]';
  const prefetched = new Set();
  let hoverTimer;
  const eligible = link => {
    if (!link || link.matches(SKIP)) return null;
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || url.hash && url.pathname === location.pathname) return null;
    if (/^\/(cart|checkout|checkouts|account|cdn|services)(\/|$)/.test(url.pathname)) return null;
    url.hash = '';
    return prefetched.has(url.href) ? null : url.href;
  };
  const prefetch = href => {
    prefetched.add(href);
    const hint = document.createElement('link');
    hint.rel = 'prefetch';
    hint.href = href;
    document.head.append(hint);
  };
  document.addEventListener('pointerover', event => {
    const href = eligible(event.target.closest?.('a[href]'));
    clearTimeout(hoverTimer);
    if (href) hoverTimer = setTimeout(() => prefetch(href), 80);
  }, {passive: true});
  document.addEventListener('pointerout', () => clearTimeout(hoverTimer), {passive: true});
  for (const type of ['touchstart', 'focusin']) {
    document.addEventListener(type, event => {
      const href = eligible(event.target.closest?.('a[href]'));
      if (href) prefetch(href);
    }, {passive: true});
  }
}

// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------
initProduct($('[data-product-root]'));
initCards();
balanceGrids();
initRecommendations();
initSearchOverflow();
restoreArchivePages().finally(rememberArchive);
$$('[data-legal-page]').forEach(initLegalPage);
window.addEventListener('resize', () => $$('[data-option]').forEach(filterSwatches), {passive: true});
document.addEventListener('shopify:section:load', event => {
  initProduct($('[data-product-root]', event.target));
  initCards(event.target);
  balanceGrids(event.target);
  initRecommendations(event.target);
});
